<template>
    <div class="weekly-view">
        <header class="dynamic-header btn-toolbar justify-content-between">
            <div class="btn-group">
                <h4>{{ title }}</h4>
            </div>
            <div class="btn-group">
                <button class="btn btn-outline-primary" v-on:click="handlePreviousWeek">
                    <i class="material-icons">arrow_back</i>
                </button>
                <button class="btn btn-outline-primary" v-on:click="handleNextWeek">
                    <i class="material-icons">arrow_forward</i>
                </button>
            </div>
        </header>
        <div class="week-grid mt-2">
            <div>
                <div class="weekday-title bg-transparent">
                    <small>&nbsp;</small><br>
                    <span>&nbsp;</span>
                </div>
                <div class="weekday-items">
                    <div v-for="time in timeList" :key="time.minutes" class="time-slice time-position" :style="time.style">
                        <span>{{ time.label }}</span>
                    </div>
                </div>
            </div>
            <div v-for="obj in currentWeekdays" :key="obj.date" class="weekday-container">
                <div class="weekday-title">
                    <small>{{ weekdayName(obj.weekday) }}</small><br>
                    <span>{{ formattedDateName(obj.fullDate) }}</span>
                </div>
                <div class="weekday-items">
                    <div v-for="time in timeList" :key="time.minutes" class="time-slice" :style="time.style">
                    </div>
                    <WeekdayItem v-for="item in dateItems(obj.fullDate)" :item="item" :minuteSetInterval="minuteSetInterval" :minuteOffset="timeListOffset" :key="item.key" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { MAX_MINUTE_DAY, minToHour } from '../helpers';
import { monthTotalDays, initialMonth, weekdayName, changeMonthByOne, monthWeekdayStart, newDate, formattedMonthName, formattedDateName } from '../../common/calendar';

const minuteIntervalSize = 25; // PX

const WeekdayItem = {
    props: {
        item: Object,
        minuteSetInterval: Number,
        minuteOffset: Number
    },
    render(createElement, ...rest){
        const { full, from, to } = this.item;
        const multiplier = minuteIntervalSize / this.minuteSetInterval;

        const style = {
            top: `${((full ? 0 : from) - this.minuteOffset) * multiplier}px`,
            height: `${(full ? MAX_MINUTE_DAY : (to - from)) * multiplier}px`,
        };

        return createElement('div', { 'class': 'weekday-item', style }, [
            this.item.render(createElement, ...rest)
        ]);
    }
};

export default {
    props: {
        initialDate: Object,
        minuteSetInterval: Number,
        provideItems: Function,
        onWeekChange: Function
    },
    data(){
        let currentWeekFirstDate = this.initialDate;
        return { currentWeekFirstDate };
    },
    computed: {
        currentMonth(){
            const { year, month } = this.currentWeekFirstDate;
            return [year, month];
        },
        currentWeekdays(){
            let { year, month, date } = this.currentWeekFirstDate;
            const total = monthTotalDays(this.currentMonth);

            const arr = [];
            let weekday = 1;

            for(; arr.length < 7 && date <= total;){
                const fullDate = newDate(year, month, date);
                arr.push({ fullDate, weekday });

                date++;
                weekday++;
            }

            const nextMonth = changeMonthByOne(this.currentMonth, 1);
            year = nextMonth[0];
            month = nextMonth[1];

            for(let currDate = 1; arr.length < 7;){
                const fullDate = newDate(year, month, currDate);
                arr.push({ fullDate, weekday });

                currDate++;
                weekday++;
            }

            return arr;
        },
        timeList(){
            const arr = [];
            for(let minutes = this.timeListOffset; minutes < MAX_MINUTE_DAY; minutes += this.minuteSetInterval){
                const label = minToHour(minutes);
                const style = { height: `${minuteIntervalSize}px` };

                arr.push({ minutes, label, style });
            }

            return arr;
        },
        timeListOffset(){
            const minutes = this.currentWeekdays.reduce((curr, obj) => this.dateItems(obj.fullDate).reduce((value, item) => {
                const from = item.from || 0;
                return value === null ? from : Math.min(from, value);
            }, curr), null);

            return Math.max((minutes || 0) - this.minuteSetInterval * 2, 0);
        },
        title(){
            return formattedMonthName(this.currentMonth);
        }
    },
    methods: {
        weekdayName(num){
            return weekdayName(num);
        },
        formattedDateName(date){
            return formattedDateName(date);
        },
        handlePreviousWeek(){
            this.moveWeek(-1);
        },
        handleNextWeek(){
            this.moveWeek(1);
        },
        moveWeek(diff){
            const { date } = this.currentWeekFirstDate;
            let { currentMonth: refMonth } = this;
            const total = monthTotalDays(refMonth);

            let incrDate = date + (diff * 7);

            if(incrDate > total){
                refMonth = changeMonthByOne(refMonth, 1);
                incrDate -= total;
            }
            else if(incrDate <= 0){
                refMonth = changeMonthByOne(refMonth, -1);
                incrDate += monthTotalDays(refMonth);
            }

            this.currentWeekFirstDate = newDate(...refMonth, incrDate);

            this.notifyCurrentWeek();
        },
        dateItems(weekday){
            return this.provideItems(weekday);
        },
        notifyCurrentWeek(){
            this.onWeekChange(this.currentWeekdays);
        }
    },
    mounted(){
        this.notifyCurrentWeek();
    },
    components: {
        WeekdayItem
    }
}
</script>

<style lang='scss'>
.week-grid {
    display: flex;
}
.weekday-container {
    position: relative;
    flex: 1 1;
    margin: 0 3px;
}
.weekday-title {
    font-weight: bold;
    text-align: center;
    border-radius: 5px 0 0 5px;
    background: #ece4fd;
}
.weekday-items {
    position: relative;
}
.time-slice {
    box-sizing: border-box;
    border: 1px solid #f8f9fa;
    border-width: 1px 0;
    text-align: right;
    font-size: .9rem;
    font-weight: bold;
    background: #ece4fd;
    color: #777;
}
.time-position {
    position: relative;
    transform: translateY(-50%);
    background-color: transparent;
}
.weekday-item {
    position: absolute;
    padding-bottom: 1px;
    width: 100%;
    
    .scheduling-list-item {
        height: 100%;
        align-items: baseline;
    }
}
</style>
